/**
 * <StickyMobileCTA> — bottom sticky call-back bar shown only on mobile
 * feature pages. Designed to lift demo-request conversion on mobile traffic.
 *
 * Behaviour:
 *  - Visible only below `lg` breakpoint (mobile/tablet) — desktop already has
 *    the hero form clearly visible.
 *  - Auto-hides while the hero lead form is in viewport (IntersectionObserver)
 *    to avoid duplicate competing CTAs.
 *  - Two actions: instant WhatsApp deeplink + "Get a callback" drawer.
 *  - On successful submit, the bar replaces itself with a thank-you state and
 *    stays dismissed for the rest of the session (sessionStorage).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Phone, MessageCircle, CheckCircle2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from './ui/drawer';
import { track } from '../services/analytics';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Owner's WhatsApp business number for the instant-tap CTA.
// (Public marketing number — safe to hardcode in the frontend bundle.)
const SALES_WHATSAPP = '919718020020';

const StickyMobileCTA = ({ slug, eyebrow, ctaSource, accentClasses }) => {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hidden, setHidden] = useState(false); // hidden when hero form is visible
  const [signupCount, setSignupCount] = useState(null); // social proof
  const dismissKey = `sticky_cta_done_${slug}`;

  // Persist a "do not show again this session" flag once they convert.
  useEffect(() => {
    if (typeof window !== 'undefined' && sessionStorage.getItem(dismissKey) === '1') {
      setSubmitted(true);
    }
  }, [dismissKey]);

  // Pull a real (not fake) signup count from the backend so the social-proof
  // chip is honest. Backend floors at 50 so we never expose a tiny number.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/demo-requests/public/stats`)
      .then((r) => {
        if (!cancelled && r.data?.recent_30d_signups_display) {
          setSignupCount(r.data.recent_30d_signups_display);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Hide the sticky bar while the hero form is on screen — observed via the
  // form's data-testid, which is stable across all feature pages.
  useEffect(() => {
    const target = document.querySelector(`[data-testid="feature-form-${slug}"]`);
    if (!target || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setHidden(entry.isIntersecting),
      { threshold: 0.25 }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [slug]);

  const handleWhatsApp = () => {
    track('cta_sticky_whatsapp_clicked', { slug, source: ctaSource });
    const msg = encodeURIComponent(
      `Hi RentKollect, I want to know more about ${eyebrow} for my PG.`
    );
    window.open(`https://wa.me/${SALES_WHATSAPP}?text=${msg}`, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clean = (mobile || '').replace(/\D/g, '').slice(-10);
    if (!/^\d{10}$/.test(clean)) {
      toast({ title: 'Please enter a valid 10-digit mobile', variant: 'destructive' });
      return;
    }
    setLoading(true);
    track('cta_sticky_callback_submitted', { slug, source: ctaSource });
    try {
      await axios.post(`${API}/demo-requests`, {
        name: name || null,
        mobile: clean,
        source: `${ctaSource}-sticky-mobile`,
        property_type: eyebrow,
        message: `Mobile sticky CTA on /features/${slug}`,
      });
      setSubmitted(true);
      sessionStorage.setItem(dismissKey, '1');
      setOpen(false);
      toast({
        title: 'Got it!',
        description: 'Our team will call you within an hour.',
      });
    } catch (err) {
      toast({
        title: 'Could not submit',
        description: err.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (hidden) return null;

  // Success state — minimal, dismissible, doesn't follow scroll forever.
  if (submitted) {
    return (
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 pt-2 pointer-events-none"
        data-testid={`sticky-cta-success-${slug}`}
      >
        <div className="pointer-events-auto mx-auto max-w-md flex items-center gap-2 rounded-xl bg-white shadow-lg ring-1 ring-green-200 px-4 py-3">
          <CheckCircle2 className="text-green-600 flex-shrink-0" size={18} />
          <p className="text-sm text-gray-800 font-medium flex-1">
            We&apos;ll WhatsApp you within an hour.
          </p>
          <button
            onClick={() => setHidden(true)}
            aria-label="Dismiss"
            className="text-gray-400 hover:text-gray-700 p-1"
            data-testid={`sticky-cta-dismiss-${slug}`}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 pt-2 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none"
        data-testid={`sticky-cta-${slug}`}
      >
        <div className="pointer-events-auto mx-auto max-w-md flex flex-col gap-1.5 rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 p-2">
          {signupCount && (
            <div
              className="flex items-center justify-center gap-1.5 text-[11px] text-gray-600 pt-0.5"
              data-testid={`sticky-cta-social-${slug}`}
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span>
                <b className="text-gray-900">{signupCount}+</b> PG owners joined in the last 30 days
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handleWhatsApp}
              aria-label="Chat on WhatsApp"
              className="flex-shrink-0 w-11 h-11 rounded-xl bg-green-50 hover:bg-green-100 text-green-700 flex items-center justify-center transition-colors"
              data-testid={`sticky-cta-whatsapp-${slug}`}
            >
              <MessageCircle size={20} />
            </button>
            <Button
              onClick={() => {
                track('cta_sticky_callback_opened', { slug, source: ctaSource });
                setOpen(true);
              }}
              className={`${accentClasses} text-white flex-1 h-11 rounded-xl font-semibold`}
              data-testid={`sticky-cta-callback-${slug}`}
            >
              <Phone size={16} className="mr-2" />
              Request a callback
            </Button>
          </div>
        </div>
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent data-testid={`sticky-cta-drawer-${slug}`}>
          <DrawerHeader className="text-left">
            <DrawerTitle>Tell us your number</DrawerTitle>
            <DrawerDescription>
              Our team will call within an hour. No spam, no auto-renewing trials.
            </DrawerDescription>
          </DrawerHeader>
          <form onSubmit={handleSubmit} className="px-4 pb-6 space-y-3">
            <Input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid={`sticky-cta-name-${slug}`}
            />
            <Input
              type="tel"
              placeholder="WhatsApp number"
              inputMode="numeric"
              maxLength={10}
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
              data-testid={`sticky-cta-mobile-${slug}`}
            />
            <Button
              type="submit"
              disabled={loading}
              className={`${accentClasses} text-white w-full h-11 rounded-xl font-semibold`}
              data-testid={`sticky-cta-submit-${slug}`}
            >
              {loading ? 'Sending…' : 'Get my callback'}
            </Button>
            <p className="text-[11px] text-gray-500 text-center">
              By submitting, you agree to receive a WhatsApp / phone call from RentKollect.
            </p>
          </form>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default StickyMobileCTA;
